import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import logoBlackSmall from '@/assets/images/logo-black-small.png'
import logoWhite from '@/assets/images/logo-white.png'
import logoScoder from '@/assets/images/clients/logo-scoder.png'
import Seo from '@/components/seo/seo'
import { breadcrumbSchema } from '@/components/seo/json-ld'
import { resolveLang } from '@/lib/site'

const clientLogos: Record<string, string> = {
  scoder: logoScoder,
}

interface BulletItem {
  title: string
  description: string
}

interface Pillar {
  num: string
  engTitle: string
  ptTitle: string
  description: string
}

interface MaturityLevel {
  level: number
  zone: 'risk' | 'transition' | 'automation'
  title: string
  description: string
}

interface CaseItem {
  label: string
  name: string
  website: string
  logoKey?: string
  casePath?: string
}

const zoneColors = {
  risk: {
    bg: 'bg-red-50',
    border: 'border-red-300',
    badge: 'bg-red-500',
    bar: 'bg-red-400',
    text: 'text-red-600',
  },
  transition: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-300',
    badge: 'bg-yellow-500',
    bar: 'bg-yellow-400',
    text: 'text-yellow-600',
  },
  automation: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-300',
    badge: 'bg-emerald-500',
    bar: 'bg-emerald-400',
    text: 'text-emerald-600',
  },
}

function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-6 md:mb-8">
      <div className="w-10 md:w-12 h-1 bg-primary rounded-full mb-3" />
      <h2 className="font-display text-xl md:text-3xl font-extrabold tracking-tight text-[#1a1a2e] uppercase">
        {title}
      </h2>
      {subtitle && <p className="text-on-surface-variant text-xs md:text-sm mt-1">{subtitle}</p>}
    </div>
  )
}

function BodyText({ html }: { html: string }) {
  return (
    <p
      className="text-sm leading-relaxed text-[#1a1a2e] mb-4 text-justify [&_strong]:font-bold [&_strong]:text-[#1a1a2e] [&_b]:font-bold [&_b]:text-[#1a1a2e] [&_a]:text-primary [&_a]:underline [&_a]:font-semibold"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

function BulletList({ items }: { items: BulletItem[] }) {
  return (
    <div className="space-y-4">
      {items.map((item, i) => (
        <div key={i} className="flex gap-3.5 items-start">
          <div className="w-2 h-2 min-w-2 bg-primary rounded-full mt-1.5" />
          <div>
            <h4 className="text-sm font-bold text-[#1a1a2e] mb-0.5">{item.title}</h4>
            <p
              className="text-xs text-on-surface-variant leading-relaxed [&_b]:font-bold [&_b]:text-[#1a1a2e]"
              dangerouslySetInnerHTML={{ __html: item.description }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

function PhaseHeader({ label, variant }: { label: string; variant: 'discovery' | 'build' }) {
  return (
    <div
      className={`rounded-lg px-4 md:px-5 py-3 md:py-3.5 font-display font-bold text-sm md:text-base tracking-wide mb-5 md:mb-6 ${
        variant === 'discovery'
          ? 'bg-gradient-to-br from-primary to-primary-container text-white'
          : 'bg-[#1a1a2e] text-primary'
      }`}
    >
      {label}
    </div>
  )
}

export default function Institucional() {
  const { t, i18n } = useTranslation()
  const lang = resolveLang(i18n.resolvedLanguage)

  const whoWeAreParagraphs = t('institucional.whoWeAre.paragraphs', {
    returnObjects: true,
  }) as string[]
  const problemItems = t('institucional.problem.items', {
    returnObjects: true,
  }) as BulletItem[]
  const pillars = t('institucional.whatWeDo.pillars', {
    returnObjects: true,
  }) as Pillar[]
  const discoveryParagraphs = t('institucional.discovery.paragraphs', {
    returnObjects: true,
  }) as string[]
  const maturityLevels = t('institucional.discovery.maturityLevels', {
    returnObjects: true,
  }) as MaturityLevel[]
  const maturityLabels = t('institucional.discovery.maturityLabels', {
    returnObjects: true,
  }) as { risk: string; transition: string; automation: string }
  const discoveryClosing = t('institucional.discovery.closing', {
    returnObjects: true,
  }) as string[]
  const buildParagraphs = t('institucional.build.paragraphs', {
    returnObjects: true,
  }) as string[]
  const operateParagraphs = t('institucional.operate.paragraphs', {
    returnObjects: true,
  }) as string[]
  const differentialItems = t('institucional.differentials.items', {
    returnObjects: true,
  }) as BulletItem[]
  const caseItems = t('institucional.cases.items', {
    returnObjects: true,
  }) as CaseItem[]

  return (
    <div className="theme-light min-h-screen bg-white md:bg-[#cbd5e0] pt-11 md:pt-12">
      <Seo
        title={t('seo.institucional.title') as string}
        description={t('seo.institucional.description') as string}
        path="/institucional"
        jsonLd={breadcrumbSchema(lang, [
          { name: 'Bravic', path: '/' },
          { name: t('seo.institucional.title') as string, path: '/institucional' },
        ])}
      />
      {/* ===== FIXED TOP BAR ===== */}
      <TopBar />

      {/* ===== PAGE 1 — COVER ===== */}
      <DocumentPage>
        <div className="relative bg-[#0f0f1a] flex flex-col justify-between flex-1 overflow-hidden">
          {/* Grid pattern */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(26,188,156,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(26,188,156,0.04) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          />
          {/* Orbs */}
          <div className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(26,188,156,0.12)_0%,transparent_70%)] pointer-events-none" />
          <div className="absolute -bottom-16 -left-20 w-[300px] h-[300px] rounded-full bg-[radial-gradient(circle,rgba(26,188,156,0.08)_0%,transparent_70%)] pointer-events-none" />
          {/* Top accent line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary-fixed to-transparent" />

          <div className="relative z-10 flex-1 flex flex-col justify-center px-6 md:px-14 py-12 md:py-16">
            <div className="font-display text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-none mb-2">
              <span className="text-[#e0e0e0]">BRAVIC</span>
              <span className="text-primary">/</span>
            </div>
            <p
              className="text-lg md:text-xl font-light text-white/60 mb-7 tracking-wide [&_strong]:text-primary [&_strong]:font-semibold"
              dangerouslySetInnerHTML={{ __html: t('institucional.tagline') }}
            />
            <div className="w-14 h-0.5 bg-primary rounded-full mb-6" />
            <p className="text-xs text-white/35 uppercase tracking-[3px]">
              {t('institucional.subtitle')} &bull; {t('institucional.year')}
            </p>
          </div>

          <div className="relative z-10 border-t border-primary/15 px-6 md:px-14 py-4 md:py-5 flex justify-between items-center">
            <p className="text-xs text-white/35">
              by <span className="text-primary font-semibold">Sugar Cane Tech</span>
            </p>
          </div>
          <div className="h-1 bg-gradient-to-r from-primary to-primary-fixed" />
        </div>
      </DocumentPage>

      {/* ===== PAGE 2 — QUEM SOMOS + PROBLEMA ===== */}
      <DocumentPage>
        <DocumentHeader />
        <div className="px-5 md:px-12 py-6 md:py-10 pb-14 md:pb-16 flex-1">
          <SectionTitle title={t('institucional.whoWeAre.title')} />
          {whoWeAreParagraphs.map((p, i) => (
            <BodyText key={i} html={p} />
          ))}

          <div className="h-8" />

          <SectionTitle
            title={t('institucional.problem.title')}
            subtitle={t('institucional.problem.subtitle')}
          />
          <BodyText html={t('institucional.problem.intro')} />
          <div className="mt-5">
            <BulletList items={problemItems} />
          </div>
        </div>
        <PageFooter page={2} />
      </DocumentPage>

      {/* ===== PAGE 3 — O QUE FAZEMOS + DISCOVERY ===== */}
      <DocumentPage>
        <DocumentHeader />
        <div className="px-5 md:px-12 py-6 md:py-10 pb-14 md:pb-16 flex-1">
          <SectionTitle
            title={t('institucional.whatWeDo.title')}
            subtitle={t('institucional.whatWeDo.subtitle')}
          />
          <BodyText html={t('institucional.whatWeDo.intro')} />

          {/* Pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-6">
            {pillars.map((pillar, i) => (
              <div
                key={i}
                className="relative bg-[#f4f6f8] rounded-lg p-5 border border-[#e2e8f0] overflow-hidden"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
                <div className="flex items-baseline gap-1.5 mb-2">
                  <span className="text-2xl font-black text-primary">{pillar.num}</span>
                  <span className="text-sm font-bold text-[#1a1a2e]">{pillar.engTitle}</span>
                </div>
                <p className="text-xs font-medium text-on-surface-variant mb-2">{pillar.ptTitle}</p>
                <p className="text-xs text-on-surface-variant leading-relaxed">
                  {pillar.description}
                </p>
              </div>
            ))}
          </div>

          <div className="h-6" />

          {/* Discovery expanded */}
          <PhaseHeader label={t('institucional.discovery.label')} variant="discovery" />
          {discoveryParagraphs.map((p, i) => (
            <BodyText key={i} html={p} />
          ))}

          {/* Maturity Scale */}
          <div className="flex gap-0.5 md:gap-1 my-4">
            {maturityLevels.map((lvl) => (
              <div
                key={lvl.level}
                className={`flex-1 h-7 md:h-8 flex items-center justify-center text-white font-bold text-[10px] md:text-xs rounded ${zoneColors[lvl.zone].bar}`}
              >
                {lvl.level}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[10px] font-bold mb-4">
            <span className={zoneColors.risk.text}>{maturityLabels.risk}</span>
            <span className={zoneColors.transition.text}>{maturityLabels.transition}</span>
            <span className={zoneColors.automation.text}>{maturityLabels.automation}</span>
          </div>

          {/* Maturity level grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-6">
            {maturityLevels.map((lvl) => {
              const colors = zoneColors[lvl.zone]
              return (
                <div
                  key={lvl.level}
                  className={`flex flex-col gap-1 p-2.5 rounded-lg border ${colors.bg} ${colors.border}`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-6 h-6 min-w-6 rounded-full flex items-center justify-center text-white font-extrabold text-[10px] ${colors.badge}`}
                    >
                      {lvl.level}
                    </div>
                    <h4 className="text-[11px] font-bold text-[#1a1a2e]">{lvl.title}</h4>
                  </div>
                  <p className="text-[10px] text-on-surface-variant leading-snug">
                    {lvl.description}
                  </p>
                </div>
              )
            })}
          </div>

          {discoveryClosing.map((p, i) => (
            <BodyText key={i} html={p} />
          ))}
        </div>
        <PageFooter page={3} />
      </DocumentPage>

      {/* ===== PAGE 4 — BUILD, OPERATE & DIFERENCIAIS ===== */}
      <DocumentPage>
        <DocumentHeader />
        <div className="px-5 md:px-12 py-6 md:py-10 pb-14 md:pb-16 flex-1">
          {/* Build */}
          <PhaseHeader label={t('institucional.build.label')} variant="build" />
          {buildParagraphs.map((p, i) => (
            <BodyText key={i} html={p} />
          ))}
          <p className="text-xs text-on-surface-variant italic mt-2 mb-8">
            {t('institucional.build.note')}
          </p>

          {/* Operate */}
          <PhaseHeader label={t('institucional.operate.label')} variant="discovery" />
          {operateParagraphs.map((p, i) => (
            <BodyText key={i} html={p} />
          ))}
          <p className="text-xs text-on-surface-variant italic mt-2 mb-8">
            {t('institucional.operate.note')}
          </p>

          {/* Diferenciais */}
          <SectionTitle title={t('institucional.differentials.title')} />
          <BulletList items={differentialItems} />
        </div>
        <PageFooter page={4} />
      </DocumentPage>

      {/* ===== PAGE 5 — CASES + CONTATO ===== */}
      <DocumentPage>
        <DocumentHeader />
        <div className="px-5 md:px-12 py-6 md:py-10 pb-14 md:pb-16 flex-1">
          <SectionTitle title={t('institucional.cases.title')} />
          <div className="space-y-4 mb-8">
            {caseItems.map((c, i) => (
              <div
                key={i}
                className="bg-[#f4f6f8] border border-[#e2e8f0] rounded-lg p-5 pl-6 border-l-4 border-l-primary flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-5 min-w-0">
                  {c.logoKey && (
                    <div className="shrink-0 flex flex-col items-center gap-1">
                      <div className="bg-[#1B2B3A] rounded-[12px] p-3 flex items-center justify-center">
                        {clientLogos[c.logoKey] ? (
                          <img
                            src={clientLogos[c.logoKey]}
                            alt={c.name}
                            className="h-10 md:h-12 w-auto object-contain"
                          />
                        ) : (
                          <span className="font-display text-lg font-extrabold text-white tracking-[2px] px-1">
                            {c.logoKey.toUpperCase()}
                          </span>
                        )}
                      </div>
                      {c.website && (
                        <a
                          href={`https://${c.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[9px] text-on-surface-variant hover:text-primary transition-colors"
                        >
                          {c.website}
                        </a>
                      )}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-primary tracking-wider mb-1">
                      {c.label}
                    </p>
                    <h4 className="text-sm md:text-base font-bold text-[#1a1a2e]">{c.name}</h4>
                  </div>
                </div>
                {c.casePath && (
                  <a
                    href={`#${c.casePath}`}
                    className="shrink-0 text-[11px] font-semibold text-white bg-[#1a1a2e] rounded-md px-4 py-2 hover:bg-[#2d2d3f] transition-colors"
                  >
                    Visualizar Case
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* Contact */}
          <SectionTitle title={t('institucional.contact.title')} />
          <div className="bg-[#1a1a2e] rounded-lg p-5 md:p-7 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-primary-fixed to-transparent" />
            <h3 className="text-lg font-bold text-white mb-4">
              {t('institucional.contact.heading')}
            </h3>
            <div className="space-y-1.5 text-sm">
              <p>
                <span className="text-primary font-semibold">Email: </span>
                <span className="text-white/60">{t('institucional.contact.email')}</span>
              </p>
              <p>
                <span className="text-primary font-semibold">
                  {t('institucional.contact.webLabel')}{' '}
                </span>
                <a
                  href={`https://${t('institucional.contact.webUrl')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 underline"
                >
                  {t('institucional.contact.webUrl')}
                </a>
              </p>
            </div>
            <a
              href="/contact"
              className="inline-block mt-5 text-xs font-semibold text-[#1a1a2e] bg-primary rounded-md px-5 py-2.5 hover:bg-primary-fixed transition-colors"
            >
              {t('institucional.contact.button')}
            </a>
            <div className="flex items-center gap-2 mt-5 pt-4 border-t border-white/10">
              <img src={logoWhite} alt="Bravic" className="h-4" />
              <span className="text-xs text-white/35">{t('institucional.contact.footerText')}</span>
            </div>
          </div>
        </div>
        <PageFooter page={5} />
      </DocumentPage>

      {/* Bottom spacing */}
      <div className="h-8" />
    </div>
  )
}

function DocumentPage({ children }: { children: ReactNode }) {
  return (
    <section className="relative bg-white md:shadow-[0_8px_40px_rgba(0,0,0,0.15)] w-full md:w-[210mm] md:min-h-[297mm] mx-auto md:my-8 overflow-hidden flex flex-col border-b border-[#e2e8f0] md:border-none md:rounded-sm">
      {children}
    </section>
  )
}

function TopBar() {
  const { i18n } = useTranslation()
  const currentLang = i18n.resolvedLanguage === 'pt-BR' ? 'PT' : 'EN'
  const toggleLang = () => {
    i18n.changeLanguage(i18n.resolvedLanguage === 'pt-BR' ? 'en' : 'pt-BR')
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a2e] border-b-2 border-primary">
      <div className="w-full md:w-[210mm] mx-auto px-5 md:px-12 py-2.5 flex justify-between items-center">
        <span className="font-display text-sm font-black tracking-tighter">
          <span className="text-white/90">BRAVIC</span>
          <span className="text-primary">/</span>
        </span>
        <button
          onClick={toggleLang}
          className="flex items-center gap-1.5 text-white/50 hover:text-primary transition-colors cursor-pointer"
        >
          <span className="material-symbols-outlined text-base">language</span>
          <span className="font-display text-[11px] font-semibold tracking-wider">
            {currentLang}
          </span>
        </button>
      </div>
    </div>
  )
}

function DocumentHeader() {
  return (
    <div className="bg-[#1a1a2e] px-5 md:px-12 py-2.5 md:py-3 flex justify-between items-center border-b-2 border-primary">
      <span className="text-[9px] md:text-[10px] font-bold text-white/70 tracking-widest uppercase">
        Documento Institucional
      </span>
      <span className="font-display text-xs md:text-sm font-black tracking-tighter">
        <span className="text-white/90">BRAVIC</span>
        <span className="text-primary">/</span>
      </span>
    </div>
  )
}

function PageFooter({ page }: { page: number }) {
  return (
    <div className="px-5 md:px-12 py-2.5 md:py-3 border-t border-[#e2e8f0] flex justify-between items-center text-[9px] text-[#718096]">
      <img src={logoBlackSmall} alt="Bravic" className="h-4" />
      <span>Página {page}</span>
    </div>
  )
}
